import { useDeviceConfigs, useDeviceTypes, useForm, useProtocols } from "hooks";
import { sortNamesBy } from "logic/sortNames";
import { BuildingId, DeviceConfigId, DeviceFamily, DeviceType, DeviceTypeId, Theme } from 'shared/models';
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getEditorTheme } from "selectors/editorSelectors";
import { DeviceConfigField } from "ui/devices/deviceConfigField/DeviceConfigField";
import { TextField } from "ui/shared/controls";
import { ProgressBar } from "ui/shared/controls/progressBar/ProgressBar";
import { ErrorMessage } from "ui/shared/messages";
import { toError } from 'shared/util/toError';
import { getErrorByProtocol } from "data/protocols";

require('./ExportThemeForm.css');

export type ExportThemeData = {
    deviceTypeId: DeviceTypeId;
    elevatorId: string;
    deviceConfigId: DeviceConfigId | null;
    buildingId: BuildingId;
    theme: Theme;
};

export type Props = ExportThemeData & {
    isExporting: boolean;
    progress: number;
    blob?: Blob;
    errorMessageKey: string | null;
    onExport(data: ExportThemeData): void;
    onClose(): void;
};

const nullValue = 'none';

export const ExportThemeForm = ({ isExporting, progress, blob, errorMessageKey, onExport, onClose, ...initialFormData }: Props) => {
    const { t } = useTranslation();

    // Prevent duplicate saves of the same file
    const fileSavedRef = useRef(false);

    const aRef = useRef<HTMLAnchorElement | null>(null);
    const [ blobUrl, setBlobUrl ] = useState('');
    const [ saveFailed, setSaveFailed ] = useState(false);
    
    const { buildingId, theme } = initialFormData;
    const { protocols } = useProtocols();

    const { data, handleChange, handleSubmit } = useForm('export-theme-form', initialFormData, onExport, { resetOn: [], nullValue });

    const { deviceTypes } = useDeviceTypes();
    const deviceTypeId = initialFormData.deviceTypeId;
    const deviceType = deviceTypes[deviceTypeId];

    const { deviceConfigs, deviceConfigIds } = useDeviceConfigs(buildingId);

    const compatibleDeviceConfigs =
        deviceConfigIds
            .map(id => deviceConfigs[id])
            .filter(dc => dc?.properties.deviceFamily === deviceType?.deviceFamily)
            .sort(sortNamesBy(dc => dc.displayName));

    const extension = getThemeExtension(deviceType);

    const now = new Date();
    const dateString = now.toISOString().replace(/[-:]/g, '').slice(0, 15) + 'Z';
    const cleanElevatorId = `${data.elevatorId.replaceAll(/\s/g, '-').replaceAll(/[^0-9a-z_-]/ig, '')}`;
    const suggestedName = `${dateString}_${cleanElevatorId}.${extension}`;

    const saveExportedTheme = useCallback(() => {
        if (fileSavedRef.current) return;
        if (typeof (window as any).showSaveFilePicker === 'undefined') {
            fileSavedRef.current = true;
            aRef.current?.click();
            onClose();
            return;
        }

        // Note: This is experimental. Exists in Chrome 91+ only so far.
        void (async () => {
            if (!blob) return;
            try {
                const result = await (window as any).showSaveFilePicker({
                    id: 'theme-export',
                    startIn: 'downloads',
                    types: [ {
                        description: t('LABEL.EXPORTED_THEME'),
                        accept: {
                            ['application/octet']: [ `.${extension}` ],
                        },
                    } ],
                    suggestedName,
                    multiple: false,
                    excludeAcceptAllOption: true,
                }) as FileSystemFileHandle | FileSystemFileHandle[];
                const fileHandle =
                    result instanceof FileSystemFileHandle
                        ? result
                        : result[0];
                const writer = await (fileHandle as any).createWritable();
                await writer.write(blob);
                await writer.close();
            }
            catch (e) {
                const err = toError(e);
                if (err.name === 'AbortError') {
                    // Keep the dialog open in case the user clicked Cancel in the save dialog
                    // (e.g. what if they canceled by accident... don't force them to republish.
                    // Instead, let the re-save if they want to. Or cancel again to really exit)
                    setSaveFailed(true);
                    return;
                }
                // Otherwise, fallback to old method if we failed to show the save dialog.
                console.error(err);
                aRef.current?.click();
            }

            // Close after a successful save operation
            fileSavedRef.current = true;
            onClose();
        })();
    }, [ blob, aRef, suggestedName, extension, fileSavedRef, onClose, setSaveFailed, t ]);

    //
    // Once the exported theme has been downloaded, we turn it into a URL
    // that we can use to allow the user to download the content.
    //
    useEffect(() => {
        if (errorMessageKey || !blob) return;
        const url = window.URL.createObjectURL(blob);
        setBlobUrl(url);
        return () => {
            window.URL.revokeObjectURL(url);
            setBlobUrl('');
        };
    }, [ errorMessageKey, blob, setBlobUrl ]);

    //
    // Similar to above, once the data has been downloaded, we attempt
    // to let the user choose where to save the file (assuming their
    // browser supports that -- Currently only in Chrome 91+). And if
    // not, then we fall back to using the URL to let them save it.
    //
    useEffect(() => {
        if (errorMessageKey || !blobUrl) return;
        saveExportedTheme();
    }, [ errorMessageKey, blobUrl, saveExportedTheme ]);

    // The export operation is done when we've got the data in blob.
    const isExportDone = !!blob;

    const protocolId = data.deviceConfigId && deviceConfigs[data.deviceConfigId]?.protocolId;
    const protocol = protocols && protocolId && protocols[protocolId];
    const canExport = !isExporting && !isExportDone && !!data.deviceConfigId && protocol && !getErrorByProtocol(protocol, t);

    if (!theme || !buildingId || !Object.keys(deviceTypes).length) {
        return null;
    }

    return (
        <form
            id="export-theme-form"
            className={ `ui form ${errorMessageKey ? 'error' : ''}` }
            onSubmit={ handleSubmit }
        >
            <TextField
                id="export-theme-form-elevator-id"
                name="elevatorId"
                type="text"
                labelKey="LABEL.ELEVATOR_ID"
                maxLength={ 32 }
                autoFocus={ true }
                disabled={ isExporting || isExportDone }
                value={ data.elevatorId }
                onChange={ handleChange }
            />

            <DeviceConfigField
                id="export-theme-form-device-config"
                name="deviceConfigId"
                deviceConfigs={ compatibleDeviceConfigs }
                buildingId={ buildingId }
                deviceTypeId={ data.deviceTypeId }
                isPI={ !!theme.requirements?.pi }
                disabled={ isExporting || isExportDone }
                value={ data.deviceConfigId }
                onChange={ handleChange }
            />

            <footer>
                { saveFailed
                    ? <button className="ui button primary _export" type="button" onClick={ saveExportedTheme }>
                        { t('COMMAND.SAVE') }
                    </button>
                    : <button className="ui button primary _export" type="submit" disabled={ !canExport }>
                        { t('COMMAND.EXPORT') }
                    </button>
                }
                <button className="ui button _cancel" type="button" onClick={ onClose }>
                    { t('COMMAND.CANCEL') }
                </button>

                { isExporting &&
                    <ProgressBar
                        id="export-theme-form-progress"
                        labelKey="LABEL.EXPORTING"
                        progress={ progress }
                    />
                }
            </footer>
            { blobUrl &&
                <a
                    ref={ aRef }
                    href={ blobUrl }
                    download={ suggestedName }
                />
            }
            <ErrorMessage className="_error" messageKey={ errorMessageKey }/>
        </form>
    );
};

export function getThemeExtension(deviceType?: DeviceType | null): string {
    switch (deviceType?.deviceFamily) {
        case DeviceFamily.Giotto: return 'gr5';
        case DeviceFamily.MatisseR4: return 'tar';
        case DeviceFamily.MatisseR5: return 'mr5';
        case DeviceFamily.Raffaello: return 'rr5';
        case DeviceFamily.TTG: return 'zip';
        case DeviceFamily.MatisseFlexR2: return 'mflex';
    }
    return 'bin';
}
